import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CREATE_ORDER_STEPS,
  changedOrderFields,
  eligibleOrderablePackages,
  feesGoStale,
  findDestination,
  gramsToKg,
  insuranceChosen,
  isStepComplete,
  modificationMayBeCharged,
  orderEditabilityFor,
  orderServiceNames,
  orderToDraft,
  rebaseOrderDraft,
  receiptsWanted,
  type CreateOrderStep,
  type OrderDraft,
  type OrderablePackage,
  type OrderService,
  type ShippingChannel,
} from '@mypoz/shared';
import { useCodeLabel, useTranslation } from '@mypoz/i18n';
import { EmptyState, Icon } from '@mypoz/ui';

import { MAX_PACKAGES_PER_ORDER } from '@/features/orders/fixtures.js';
import { ChangeReview } from '@/features/orders/components/ChangeReview.js';
import { CatalogErrorBanner } from '@/features/orders/components/CatalogErrorBanner.js';
import { ChannelScopeNotice, ChannelStep } from '@/features/orders/components/ChannelStep.js';
import { DeliveryForm } from '@/features/orders/components/DeliveryForm.js';
import { LockedStep } from '@/features/orders/components/LockedStep.js';
import { ORDERS_ROUTE } from '@/shell/navigation.js';
import { PackagesStep } from '@/features/orders/components/PackagesStep.js';
import { StepIndicator } from '@/features/orders/components/StepIndicator.js';
import { OrderFlowBar, OrderFlowHeader } from '@/features/orders/components/OrderFlowLayout.js';
import { TRANSPORT_TYPE, usePackages } from '@/features/packages/index.js';
import { buttonClass } from '@/lib/styles.js';
import { useSelectionWeight } from '@/features/orders/useSelectionWeight.js';
import { useFormat } from '@/lib/format.js';
import { useScrollToTopOnChange } from '@/lib/scroll.js';
import { useOrderCatalog } from '@/features/orders/catalogContext.js';
import { useOrderServices } from '@/features/orders/orderServicesContext.js';
import { useOrder, useOrders, type OrderModifyOutcome } from '@/features/orders/context.js';
import { useScopedChannels } from '@/features/orders/useScopedChannels.js';
import { useScopedServices } from '@/features/orders/useScopedServices.js';
import { useWarehouses } from '@/features/warehouses/index.js';

/** Stable empty selection, for when the channel is locked and nothing needs looking up. */
const NO_PACKAGES: readonly OrderablePackage[] = [];

/** `?step=delivery` so the detail panel's Edit links land where the reader was looking. */
function stepFromParam(value: string | null): number {
  const index = CREATE_ORDER_STEPS.indexOf(value as CreateOrderStep);
  return index === -1 ? 0 : index;
}

/**
 * Edit order — the create flow run backwards over an order that already exists.
 *
 * The same four steps and the same components, with three differences that matter. The draft is
 * read out of the order rather than started empty. Steps the warehouse has already acted on are
 * read-only, with the reason stated. And the footer commits an amendment rather than a submission,
 * which is why Save stays dead until something has actually changed: a Save that always looks
 * available teaches people to press it, and pressing it should mean something.
 */
export function EditOrder() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const weightLabel = useSelectionWeight();
  const codeLabel = useCodeLabel();
  const navigate = useNavigate();
  const { orderNumber } = useParams();
  const order = useOrder(orderNumber);
  const { detailStatus, loadDetail, status, modify } = useOrders();
  const { packages, status: packagesStatus, reload: reloadPackages } = usePackages();
  const catalog = useOrderCatalog();
  const { services } = useOrderServices();
  const { warehouses } = useWarehouses();
  const [searchParams] = useSearchParams();

  /*
    An order's parcels and address live only on its detail record, which is fetched lazily when a
    card is expanded (MYP-53) — so arriving here straight from the Edit button, without having
    opened the card, means `order.packages` is empty and `order.address` is undefined.

    Seeding the draft from that is not merely an empty-looking form: an empty `packageIds` on an
    editable order reads as "the customer unticked everything" — `toOrderModifyParams` would list
    every parcel in `delPkgId` — so saving a changed remark would strip the order's parcels. So the
    fetch is started here too, and the draft waits for it.
  */
  /*
    A row without the vendor's id has no detail to fetch; it is refused below as unsaveable rather
    than left waiting forever. Everything else waits for its detail.
  */
  const needsDetail = order !== undefined && order.vendorOrderId !== undefined;
  const detail = orderNumber ? detailStatus(orderNumber) : 'idle';
  const ready = !needsDetail || detail === 'ready';

  /*
    Depends on the vendor id, not just the order number: `loadDetail` resolves the id from the
    order list, so arriving here by URL before that list has landed finds nothing to fetch. Keyed
    on mount alone this effect would fire once into an empty list and never run again, leaving the
    form waiting forever — which is exactly what it did.
  */
  useEffect(() => {
    if (orderNumber && order?.vendorOrderId) loadDetail(orderNumber);
  }, [orderNumber, order?.vendorOrderId, loadDetail]);

  const [step, setStep] = useState(() => stepFromParam(searchParams.get('step')));
  // MYP-118: every way of changing step (Continue, the step strip, Confirm's Edit links, Submit
  // sending the reader back) opens the new step at its top, with focus at its start.
  const stepTop = useRef<HTMLDivElement>(null);
  useScrollToTopOnChange(step, stepTop);
  /*
    Seeded once, and only once the record is complete: re-deriving it from `order` on every render
    would throw away each keystroke the moment the provider re-rendered for any other reason.

    Seeded twice over, though, and both halves are load-bearing. The initializer covers the case
    where the record is already in hand on the first render — a vendor order whose detail was cached
    when its card was expanded. Without it those mount with
    `draft` null, which the guard below reads as "no such order", and the page paints "we can't
    find this order" for a frame before the effect corrects it. The effect covers the other case,
    where the detail arrives after mount, and does nothing once a draft exists.
  */
  const [storedDraft, setDraft] = useState<OrderDraft | null>(() =>
    order && ready ? orderToDraft(order) : null,
  );

  useEffect(() => {
    // The `current === null` guard is what keeps this a seed rather than a reset: once the form
    // has a draft, nothing here touches it again, however many times the provider re-renders.
    setDraft((current) => (current === null && order && ready ? orderToDraft(order) : current));
  }, [order, ready]);

  /*
    What this render works from: the stored draft once there is one, and otherwise the seed the
    effect above is about to store.

    The derivation is not redundant with the initializer. On the cold path — arriving by URL, with
    neither the order list nor the detail record in hand — `ready` turns true on a later render
    than the one that mounted, and the effect that seeds from it does not run until after that
    render has committed. For that one render the state is still null, which the guard below reads
    as "no such order", and the page paints "we can't find this order" before correcting itself.
    Deriving closes the gap; the state is what makes edits stick afterwards.

    Nothing can be typed in that window — it is a single commit with no paint the user can act on —
    so `setDraft`'s updaters below, which no-op while the state is null, cannot lose a keystroke.
  */
  const seeded = storedDraft ?? (order && ready ? orderToDraft(order) : null);

  /*
    A vendor order's payment method and promo code are shown locked (`order_modify` takes neither),
    so they always read from the order itself. Otherwise a stale save that brought in the
    warehouse's newer value would leave the draft's old one looking like an edit — an armed Save
    and a review line for a change that can never be sent.
  */
  const draft =
    seeded && order && order.vendorOrderId !== undefined
      ? { ...seeded, paymentMode: order.paymentMode, couponCode: order.couponCode }
      : seeded;
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  /*
    Saving is a request now (MYP-86), so it has a pending state, a guard against a second click
    that the button's disabled state alone cannot give (two clicks can land before the re-render),
    and an outcome to show when it did not save.
  */
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveOutcome, setSaveOutcome] = useState<Exclude<
    OrderModifyOutcome,
    { kind: 'saved' }
  > | null>(null);

  /*
    Any edit clears the last outcome: the message was about the draft as it was, and leaving
    "the warehouse refused this" under a different draft reads as a verdict on the new one.
  */
  const [servicesCleared, setServicesCleared] = useState(false);
  const patch = useCallback((next: Partial<OrderDraft>) => {
    setSaveOutcome(null);
    if (next.serviceIds) setServicesCleared(false);
    setDraft((current) => (current ? { ...current, ...next } : current));
  }, []);

  /**
   * Everything tickable: the warehouse's unassigned packages plus the ones already on this order.
   *
   * Without the second half, opening an order would show its own parcels missing from the list
   * they are supposed to be ticked in — and unticking one would be the only possible edit.
   */
  const eligible = useMemo(
    () => eligibleOrderablePackages(packages, catalog.eligiblePackageStatusCodes),
    [packages, catalog.eligiblePackageStatusCodes],
  );

  const pool = useMemo<readonly OrderablePackage[]>(() => {
    if (!order) return eligible;

    const known = new Set(eligible.map((item) => item.id));
    const own = order.packages
      .filter((line) => !known.has(line.packageId))
      .map((line) => ({
        id: line.packageId,
        trackingNumber: line.trackingNumber,
        itemName: line.itemName,
        quantity: 1,
        weightGrams: line.weightGrams,
        warehouseId: line.warehouseId,
        goodsType: line.goodsType,
      }));

    return [...own, ...eligible];
  }, [order, eligible]);

  /*
    Whether this order's destination exists in the list the cascade can offer.

    MYP-53 slice 2 wires `country`/`province`/`city` to the same vendor's-own numeric codes an
    order record carries (`countryid: "1"`), so a vendor order's destination is now, in practice,
    always found here. The guard stays rather than being deleted: it is what protects against the
    one case that would still slip through — a country code the catalog fetch failed to return, or
    one the vendor has since retired. A form that cannot display the current value must not be the
    thing that sets the next one, since every save from it would rewrite the address from three
    empty selects.
  */
  const destinationKnown =
    draft?.countryCode === undefined ||
    findDestination(catalog.countries, draft.countryCode) !== undefined;

  const editable = order ? orderEditabilityFor(order) : undefined;
  /*
    Every order is the vendor's now that new ones go through `orders_create` (MYP-91), and is saved
    through `order_modify`. One without the vendor's id cannot be, and is refused below.
  */
  const sendsToWarehouse = order?.vendorOrderId !== undefined;
  const changed = order && draft ? changedOrderFields(order, draft) : [];
  const feesStale = order ? feesGoStale(order, changed) : false;

  const togglePackage = (packageId: string) => {
    setSaveOutcome(null);
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        packageIds: current.packageIds.includes(packageId)
          ? current.packageIds.filter((id) => id !== packageId)
          : current.packageIds.length >= MAX_PACKAGES_PER_ORDER
            ? current.packageIds
            : [...current.packageIds, packageId],
      };
    });
  };

  /*
    The channels this order's packages can use (MYP-87) — looked up only while the channel is still
    the customer's to change. The order's own channel is never cleared: 69 of 284 orders on the
    test account use a channel the lookup does not return, and an edit of their remark must not
    take it away. A channel picked in this session that the new selection does not offer is.
  */
  const packageIdsKey = draft?.packageIds.join(',') ?? '';
  const scopePackages = useMemo(
    () =>
      editable?.channel
        ? pool.filter((item) => packageIdsKey.split(',').includes(item.id))
        : NO_PACKAGES,
    [editable?.channel, pool, packageIdsKey],
  );
  const scoped = useScopedChannels(scopePackages);
  const [channelCleared, setChannelCleared] = useState(false);
  const pickedChannel =
    draft?.channelId !== undefined && draft.channelId !== order?.channelId
      ? draft.channelId
      : undefined;
  const channelWithdrawn =
    pickedChannel !== undefined &&
    (scoped.result.kind !== 'ok' ||
      (scoped.status === 'ready' && !scoped.channels.some((item) => item.id === pickedChannel)));

  useEffect(() => {
    if (!channelWithdrawn) return;
    setDraft((current) => (current ? { ...current, channelId: undefined } : current));
    setChannelCleared(true);
  }, [channelWithdrawn]);

  /*
    Services are per warehouse (MYP-89): the one the channel lookup settled on, or — when the
    packages are locked and nothing is looked up — the order's own. The order's own services stay
    on it even when the warehouse no longer offers them (retired, or moved to another warehouse);
    only a service ticked in this session that the warehouse does not offer is cleared.
  */
  const serviceWarehouse =
    scoped.result.kind === 'ok'
      ? scoped.result.scope.warehouseId
      : editable && !editable.packages
        ? order?.warehouseId
        : undefined;
  const keptServices = useMemo(
    () => (order ? orderServiceNames(order.serviceIds, services, order.orderedServices) : []),
    [order, services],
  );
  const servicesView = useScopedServices(serviceWarehouse, keptServices);
  const withdrawnServices =
    servicesView.kind === 'ready' && draft
      ? draft.serviceIds
          .filter(
            (id) =>
              !order?.serviceIds.includes(id) &&
              !servicesView.offered.some((service) => service.id === id),
          )
          .join(',')
      : '';

  useEffect(() => {
    if (withdrawnServices === '') return;
    const withdrawn = withdrawnServices.split(',');
    setDraft((current) =>
      current
        ? { ...current, serviceIds: current.serviceIds.filter((id) => !withdrawn.includes(id)) }
        : current,
    );
    setServicesCleared(true);
  }, [withdrawnServices]);

  /** Every service id on this page with a name: the catalog's, then the order's own names. */
  const knownServices = useMemo<readonly OrderService[]>(() => {
    const extra = keptServices
      .filter((kept) => !services.some((service) => service.id === kept.id))
      .map((kept) => ({ id: kept.id, name: kept.name, description: '' }));
    return [...services, ...extra];
  }, [services, keptServices]);

  /**
   * Everything a channel id on this page could name: the offered list, the `channels` dictionary,
   * and last the order's own channel under the name its record gives (`carrier`), so an order
   * whose channel neither list carries still shows one.
   */
  const knownChannels = useMemo<readonly ShippingChannel[]>(() => {
    const merged = [...scoped.channels];
    const seen = new Set(merged.map((item) => item.id));
    for (const item of catalog.channels) {
      if (!seen.has(item.id)) {
        merged.push(item);
        seen.add(item.id);
      }
    }
    if (order?.channelId && !seen.has(order.channelId)) {
      merged.push({
        id: order.channelId,
        name: order.carrier ?? order.channelId,
        transportType: '',
        weightBasis: '',
        transitDaysMin: 0,
        transitDaysMax: 0,
      });
    }
    return merged;
  }, [scoped.channels, catalog.channels, order?.channelId, order?.carrier]);

  /*
    Two ways to arrive at nothing to edit: a number that matches no order, and an order the
    warehouse has moved past. They are different situations and get different words — "we cannot
    find this" is not the same as "this has already shipped".
  */
  /*
    Three states before the form, and they are not the same thing.

    Waiting — either the list has not arrived (so this order cannot be looked up yet) or its detail
    record has not. Saying "we cannot find this order" during either would be a lie the reader acts
    on by leaving.
  */
  if (status === 'loading' || (order && !ready && detail !== 'error')) {
    return <EmptyState icon="hourglass_empty" title={t('orders.edit.loading')} />;
  }

  /*
    Failed — and this one deliberately refuses to open the form. Without the detail record the
    draft would seed with no parcels and no address, which a save reads as the customer having
    removed them: one saved remark and the order loses both. A retry is the only honest thing to
    offer.
  */
  if (order && detail === 'error') {
    return (
      <EmptyState
        icon="cloud_off"
        title={t('orders.edit.loadFailed')}
        description={t('orders.edit.loadFailedBody')}
        action={
          <button
            type="button"
            onClick={() => orderNumber && loadDetail(orderNumber)}
            className={buttonClass('secondary')}
          >
            <Icon name="refresh" size={18} />
            {t('orders.edit.retry')}
          </button>
        }
      />
    );
  }

  if (!order || !draft || !editable) {
    return (
      <EmptyState
        icon="search_off"
        title={t('orders.edit.notFound')}
        description={t('orders.edit.notFoundBody')}
        action={
          <Link to={ORDERS_ROUTE} className={buttonClass('secondary')}>
            {t('orders.edit.backToOrders')}
          </Link>
        }
      />
    );
  }

  /*
    An order whose record lacks what `order_modify` requires (its vendor id, its `ordertype`).
    Opening the form would invite an edit that can only fail, or worse be sent with a guessed type.
  */
  if (!sendsToWarehouse || !order.modifyBase) {
    return (
      <EmptyState
        icon="lock"
        title={t('orders.edit.cannotSave')}
        description={t('orders.edit.cannotSaveBody')}
        action={
          <Link to={ORDERS_ROUTE} className={buttonClass('secondary')}>
            {t('orders.edit.backToOrders')}
          </Link>
        }
      />
    );
  }

  if (!editable.packages && !editable.channel && !editable.delivery) {
    return (
      <EmptyState
        icon="lock"
        title={t('orders.edit.locked')}
        description={t('orders.edit.lockedBody')}
        action={
          <Link to={ORDERS_ROUTE} className={buttonClass('secondary')}>
            {t('orders.edit.backToOrders')}
          </Link>
        }
      />
    );
  }

  const selected = pool.filter((item) => draft.packageIds.includes(item.id));
  const channel = knownChannels.find((item) => item.id === draft.channelId);

  /*
    Receipts on an order being edited (MYP-140). `order_modify` takes none (MYP-96), so the ones
    the order already has are shown, and only a package that needs one but has none says it can
    only be attached on a new order. Insurance added here would need a receipt on every package,
    which this endpoint cannot send — so Save holds, and says why, rather than saving an insured
    order with no receipts behind it.
  */
  const savedReceipts: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
    order.packages.map((line) => [line.packageId, line.receiptPaths ?? []]),
  );
  const insuredNow = insuranceChosen(draft.serviceIds, knownServices);
  const insuranceAdded = insuredNow && !insuranceChosen(order.serviceIds, knownServices);
  const wantsReceipt = (item: OrderablePackage) =>
    receiptsWanted(
      item.warehouseId,
      warehouses.find((warehouse) => warehouse.id === item.warehouseId)?.country,
      insuredNow,
    );
  const insuranceBlocked =
    insuranceAdded && selected.some((item) => (savedReceipts[item.id]?.length ?? 0) === 0);
  /*
    A package change whose channels cannot be looked up (two warehouses, mixed goods types, a
    package with no goods type) cannot be saved: nothing can say which channel it may use.
  */
  const scopeBlocked =
    editable.packages && changed.includes('packages') && scoped.result.kind !== 'ok';

  const steps = [
    t('orders.newOrder.step.packages'),
    t('orders.newOrder.step.channel'),
    t('orders.newOrder.step.delivery'),
    t('orders.edit.step.review'),
  ];

  const canSave =
    changed.length > 0 &&
    isStepComplete(draft, 'confirm') &&
    !scopeBlocked &&
    !insuranceBlocked &&
    !saving;

  const leave = () => navigate(ORDERS_ROUTE);

  const save = async () => {
    if (savingRef.current) return;

    savingRef.current = true;
    setSaving(true);
    setSaveOutcome(null);

    const packagesChanged = changed.includes('packages');
    const outcome = await modify(order.orderNumber, draft);

    savingRef.current = false;
    setSaving(false);

    if (outcome.kind === 'saved') {
      // Parcels added to or taken off the order change which packages are free to order.
      if (packagesChanged) reloadPackages();
      navigate(ORDERS_ROUTE, { state: { saved: order.orderNumber } });
      return;
    }

    /*
      The order moved on at the warehouse. Its newer record is already on screen; the draft moves
      onto it too, keeping only what the customer edited — see `rebaseOrderDraft` for why keeping
      the old draft whole would quietly undo the warehouse's change on the next save.
    */
    if (outcome.kind === 'stale') {
      const opened = order;
      setDraft((current) =>
        current ? rebaseOrderDraft(opened, outcome.current, current) : current,
      );
    }

    setSaveOutcome(outcome);
  };

  return (
    <>
      <OrderFlowHeader
        title={t('orders.edit.title', { number: order.orderNumber })}
        stepper={<StepIndicator steps={steps} current={step} onSelect={setStep} />}
        actions={
          <Link
            to={ORDERS_ROUTE}
            className="inline-flex min-h-11 shrink-0 items-center gap-1.5 text-sm font-semibold text-brand transition-colors hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0"
          >
            <Icon name="close" size={18} />
            {t('orders.newOrder.cancel')}
          </Link>
        }
      />

      {sendsToWarehouse && modificationMayBeCharged(order) ? (
        <p className="mb-5 border-l-2 border-warning pl-3 text-sm text-ink">
          {t('orders.edit.modificationFee')}
        </p>
      ) : null}

      <CatalogErrorBanner />

      <div ref={stepTop} tabIndex={-1} className="outline-none">
        {step === 0 ? (
          editable.packages ? (
            <>
              <PackagesStep
                pool={pool}
                selectedIds={draft.packageIds}
                onToggle={togglePackage}
                /*
                  The upload button shows but is locked: `order_modify` takes no `packageinfo`
                  (MYP-96), so a receipt picked while editing would have nowhere to go. Receipts are
                  attached when the order is created; the ones it kept are shown (MYP-140).
                */
                receiptsLocked
                wantsReceipt={wantsReceipt}
                savedReceipts={savedReceipts}
                status={packagesStatus === 'error' ? 'error' : 'ready'}
                onRetry={reloadPackages}
              />
              <ChannelScopeNotice result={scoped.result} className="mt-4" />
            </>
          ) : (
            <LockedStep note={t('orders.edit.lockedPackages')}>
              {order.packages.map((line) => (
                <p key={line.packageId} className="text-ink">
                  <span className="font-semibold">{line.trackingNumber}</span>
                  <span className="text-muted">
                    {' · '}
                    {line.itemName}
                    {line.weightGrams === undefined
                      ? ''
                      : ` · ${t('orders.newOrder.weightKg', {
                          value: fmt.number(gramsToKg(line.weightGrams), 1),
                        })}`}
                  </span>
                </p>
              ))}
            </LockedStep>
          )
        ) : null}

        {step === 1 ? (
          editable.channel ? (
            <ChannelStep
              scoped={scoped}
              selectedId={draft.channelId}
              selectedName={draft.channelId === order.channelId ? order.carrier : undefined}
              cleared={channelCleared}
              onSelect={(channelId) => {
                setChannelCleared(false);
                patch({ channelId });
              }}
            />
          ) : (
            <LockedStep note={t('orders.edit.lockedChannel')}>
              {channel ? (
                <>
                  <p className="font-semibold text-ink">{channel.name}</p>
                  {channel.transportType === '' ? null : (
                    <p className="text-muted">
                      {codeLabel(TRANSPORT_TYPE, channel.transportType)} ·{' '}
                      {t('orders.newOrder.channel.transitDays', {
                        min: fmt.number(channel.transitDaysMin),
                        max: fmt.number(channel.transitDaysMax),
                      })}
                    </p>
                  )}
                </>
              ) : null}
            </LockedStep>
          )
        ) : null}

        {step === 2 ? (
          destinationKnown ? (
            <DeliveryForm
              countries={catalog.countries}
              provincesFor={catalog.provincesFor}
              provincesStatus={catalog.provincesStatus}
              citiesFor={catalog.citiesFor}
              citiesStatus={catalog.citiesStatus}
              loadCities={catalog.loadCities}
              servicesView={servicesView}
              servicesCleared={servicesCleared}
              draft={draft}
              onPatch={patch}
              locked={{
                payment: t('orders.edit.paymentLocked'),
                coupon: t('orders.edit.couponLocked'),
              }}
            />
          ) : (
            <LockedStep note={t('orders.edit.lockedDelivery')}>
              {order.address ? (
                <>
                  <p className="font-semibold text-ink">
                    {[order.address.recipient, order.address.phone].filter(Boolean).join(' · ')}
                  </p>
                  {order.address.line1 ? <p className="text-muted">{order.address.line1}</p> : null}
                  <p className="text-muted">
                    {[
                      [order.address.postcode, order.address.city].filter(Boolean).join(' '),
                      order.address.province,
                      order.address.country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                </>
              ) : (
                <p className="text-muted">{t('orders.detail.none')}</p>
              )}
            </LockedStep>
          )
        ) : null}

        {(step === 2 || step === 3) && insuranceBlocked ? (
          <p role="status" className="mt-4 border-l-2 border-warning pl-3 text-sm text-ink">
            {t('orders.edit.insuranceNeedsReceipts')}
          </p>
        ) : null}

        {step === 3 ? (
          <ChangeReview
            order={order}
            draft={draft}
            changed={changed}
            feesStale={feesStale}
            sendsToWarehouse={sendsToWarehouse}
            pool={pool}
            channels={knownChannels}
            services={knownServices}
            countries={catalog.countries}
            provincesFor={catalog.provincesFor}
            citiesFor={catalog.citiesFor}
            onEdit={setStep}
          />
        ) : null}
      </div>

      {/*
        One footer for every step rather than a Continue that walks you to the end. An edit is
        rarely a journey — you came for the remark, you change the remark, and being made to click
        through two more steps to save it is the flow arguing with you.
      */}
      <OrderFlowBar>
        <div className="w-full">
          {saveOutcome ? <SaveOutcomeMessage outcome={saveOutcome} /> : null}

          {confirmingDiscard ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <p className="text-sm text-ink sm:mr-auto">{t('orders.edit.discardConfirm')}</p>
              <button
                type="button"
                onClick={() => setConfirmingDiscard(false)}
                className={buttonClass('secondary')}
              >
                {t('orders.edit.keepEditing')}
              </button>
              <button type="button" onClick={leave} className={buttonClass('danger')}>
                {t('orders.edit.discardConfirmed')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              {/*
              Why Save is dead, said out loud. A disabled button with no explanation is a puzzle,
              and the reasons differ: nothing changed yet, or which step is missing something.
            */}
              <p className="text-sm text-muted tabular-nums sm:mr-auto">
                {/* MYP-131: the selection summary lives here now, not under the title. */}
                <span className="font-semibold text-ink">
                  {t('orders.newOrder.summary', {
                    selected: fmt.number(selected.length),
                    weight: weightLabel(selected),
                  })}
                </span>
                {' · '}
                {changed.length === 0
                  ? t('orders.edit.noChanges')
                  : !isStepComplete(draft, 'packages')
                    ? t('orders.edit.incompletePackages')
                    : scopeBlocked
                      ? t('orders.edit.scopeBlocked')
                      : insuranceBlocked
                        ? t('orders.edit.insuranceNeedsReceiptsShort')
                        : !isStepComplete(draft, 'channel')
                          ? t('orders.edit.incompleteChannel')
                          : !isStepComplete(draft, 'delivery')
                            ? t('orders.edit.incomplete')
                            : t('orders.edit.changeCount', { count: changed.length })}
              </p>

              <button
                type="button"
                disabled={saving}
                onClick={() => (changed.length > 0 ? setConfirmingDiscard(true) : leave())}
                className={buttonClass(
                  'secondary',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {t('orders.edit.discard')}
              </button>

              <button
                type="button"
                disabled={!canSave}
                aria-busy={saving}
                onClick={() => void save()}
                className={buttonClass(
                  'primary',
                  'px-8 disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {saving ? t('orders.edit.saving') : t('orders.edit.save')}
              </button>
            </div>
          )}
        </div>
      </OrderFlowBar>
    </>
  );
}

/**
 * Why a save did not go through, said where the Save button is.
 *
 * Inline rather than a toast: the draft is still on screen and the customer's next move is on this
 * form — fix something, or press Save again. The vendor's own reason is shown beneath a refusal
 * because `status: 0` is one code for every kind of refusal, and its `msg` is the only thing that
 * says which (MYP-86); it is the vendor's Chinese, so it sits under a translated line, not alone.
 */
function SaveOutcomeMessage({
  outcome,
}: {
  outcome: Exclude<OrderModifyOutcome, { kind: 'saved' }>;
}) {
  const { t } = useTranslation();

  let detail: string | undefined;
  if (outcome.kind === 'refused') {
    detail =
      outcome.detail !== ''
        ? t('orders.edit.saveError.vendorSaid', { detail: outcome.detail })
        : t('orders.edit.saveError.code', { code: outcome.code });
  }

  return (
    <div role="alert" className="mb-4 rounded-md border border-danger/30 bg-danger/5 px-3 py-2">
      <p className="text-sm text-danger">{t(`orders.edit.saveError.${outcome.kind}`)}</p>
      {detail ? <p className="mt-1 text-xs text-muted">{detail}</p> : null}
    </div>
  );
}
